import { isBrowser } from '@/services/Utilities/Runtime';
import type { WrapperToasterEmbedProps } from '@toaster/pantry/types';
import type { MutableRefObject } from 'react';
import { useEffect } from 'react';
export const toasterEmbededParams = (colorScheme?: 'light' | 'dark') => {
  const params: Partial<WrapperToasterEmbedProps> = {
    variant: colorScheme ?? 'light',
    padding: false,
    titles: true
  };
  if (!colorScheme && isBrowser && window.matchMedia('(prefers-color-scheme:dark)').matches) {
    params.variant = 'dark';
    params.padding = true;
  }
  return params;
};
interface ToasterIframe extends HTMLIFrameElement {
  fallbackPictureElem?: HTMLPictureElement;
}
const chartSelector = 'picture[data-toaster-uuid]';

/**
 * Scans the page for charts and fetches Iframes from the Toaster API.
 * Images are then swapped out for the iframe equivalent
 * @see <https://graphics-preview.bloomberg.com/graphics/toaster-embed-examples/>
 * @return The wrapper element ref you want the search scoped down to
 */
export const useToaster = (ref: MutableRefObject<HTMLElement>): MutableRefObject<any> => {
  useEffect(() => {
    if (!ref.current) {
      return;
    }
    const charts = ref.current.querySelectorAll(chartSelector);
    if (charts.length === 0) {
      return;
    }
    import('@toaster/pantry').then(({
      ToasterEmbed
    }) => {
      if (!ref.current) {
        // Might have been unmounted by then
        return;
      }
      const charts_0 = ref.current.querySelectorAll(chartSelector); // Re-query in case the DOM has changed
      for (const pictureElm of charts_0 as NodeListOf<HTMLPictureElement>) {
        const uuid = pictureElm.dataset['toasterUuid'];
        const mountElement = document.createElement('div');
        const chart = new ToasterEmbed(mountElement, {
          ...toasterEmbededParams(),
          id: uuid,
          onReady: () => {
            iframe.style.height = '';
            if (iframe.fallbackPictureElem) {
              iframe.fallbackPictureElem.dataset.toasterFallbackPicture = ''; // for @media print style
              // Set once and done, no longer need to keep the reference to it
              delete iframe.fallbackPictureElem;
            }
          }
        });

        // Dynamically build up the iframe
        const iframe = chart.iframe as ToasterIframe;
        iframe.dataset.toasterIframe = ''; // See base.ts for @media print style
        iframe.style.height = '0'; // Hide until it successfully loaded
        iframe.style.overflow = 'hidden';

        // Toaster lives on bloomberg.com which is blocked in China.
        // Store reference to fallback image to be removed once iframe has loaded successfully
        iframe.fallbackPictureElem = pictureElm;
        pictureElm.parentElement.insertBefore(iframe, pictureElm);
        delete pictureElm.dataset.toasterUuid; // Prevent being processed by useToaster again
      }
    });
  }, [ref]);
  return ref;
};